import { useContext, useState, useRef, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { IonIcon } from '@ionic/react';
import {
  terminalOutline, eyeOutline, notificationsOutline, rocketOutline,
  flaskOutline, bugOutline, hammerOutline, analyticsOutline, searchOutline,
  codeSlashOutline, cloudDownloadOutline, cloudUploadOutline, chatbubbleEllipsesOutline,
  documentTextOutline, bandageOutline, gitPullRequestOutline, gitNetworkOutline,
  mailOutline, shieldCheckmarkOutline, sparklesOutline, checkmarkDoneOutline,
  serverOutline, listOutline, sendOutline, refreshOutline, trashOutline,
} from 'ionicons/icons';
import { WorkflowActionsContext } from '../contexts/WorkflowActionsContext';
import { CartoonPreflight, CartoonExecuteLive, CartoonGenerateScript } from './CartoonWorkflowIcons';
import './WorkflowNode.css';

// Semantic icon picker: maps a node's type/label keywords to a fitting icon so
// every template gets meaningful glyphs instead of a generic terminal. First
// match wins; order from specific → generic. Falls back to terminalOutline.
const ICON_RULES = [
  [/review|inspect|audit/, eyeOutline],
  [/pull[\s_-]?request|\bpr\b|merge/, gitPullRequestOutline],
  [/notify|notification|alert/, notificationsOutline],
  [/deploy|release|publish|ship/, rocketOutline],
  [/upload|push|sync[\s_-]?up/, cloudUploadOutline],
  [/clone|fetch|download|pull|checkout/, cloudDownloadOutline],
  [/clone[\s_-]?repo|git|repo|branch/, gitNetworkOutline],
  [/preflight|test|verify|validate|qa/, flaskOutline],
  [/bug|triage|error|sentry|crash/, bugOutline],
  [/build|compile|bundle|package/, hammerOutline],
  [/analy|metric|report|score|stat/, analyticsOutline],
  [/search|scan|find|discover|lookup/, searchOutline],
  [/generate[\s_-]?script|script|codegen|code/, codeSlashOutline],
  [/comment|reply|respond|message|chat/, chatbubbleEllipsesOutline],
  [/summar|document|doc|write[\s_-]?up|draft/, documentTextOutline],
  [/fix|patch|repair|autofix|remediat/, bandageOutline],
  [/email|mail/, mailOutline],
  [/security|auth|permission|guard|protect/, shieldCheckmarkOutline],
  [/enrich|enhance|augment|polish/, sparklesOutline],
  [/finalize|complete|done|approve|confirm/, checkmarkDoneOutline],
  [/execute|run|server|host|provision/, serverOutline],
  [/list|collect|gather|enumerate/, listOutline],
  [/send|dispatch|post|emit/, sendOutline],
  [/refresh|retry|reload|update/, refreshOutline],
  [/delete|remove|cleanup|purge/, trashOutline],
];

function pickNodeIcon(nodeType, label) {
  const hay = `${nodeType || ''} ${label || ''}`.toLowerCase();
  for (const [re, icon] of ICON_RULES) {
    if (re.test(hay)) return icon;
  }
  return terminalOutline;
}

const EditableLabel = ({ label, editing, setEditing, editValue, setEditValue, commitRename, inputRef, className }) => (
  <div className={`gwe-workflow-node__label-wrap ${className || ''}`}>
    <div
      className="gwe-workflow-node__label gwe-workflow-node__label--editable"
      style={{ visibility: editing ? 'hidden' : 'visible' }}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
    >
      {label}
    </div>
    {editing && (
      <input
        ref={inputRef}
        className="gwe-workflow-node__label-input"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commitRename}
        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setEditValue(label); setEditing(false); } }}
        onClick={(e) => e.stopPropagation()}
      />
    )}
  </div>
);

export const WorkflowNode = ({ data, isConnectable }) => {
  const {
    label, status = 'pending',
    showAddButton = false, nodeId, toolCount = 0,
    customCode = null, nodeType
  } = data;
  const hasCustomCode = !!customCode;
  const actions = useContext(WorkflowActionsContext);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const inputRef = useRef(null);

  useEffect(() => { setEditValue(label); }, [label]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== label) {
      actions?.onRenameNode(nodeId, trimmed);
    } else {
      setEditValue(label);
    }
    setEditing(false);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    actions?.onNodeContextMenu(nodeId, { x: e.clientX, y: e.clientY });
  };

  const statusColors = {
    pending: '#8c8c8c',
    running: '#4a90e2',
    completed: '#52c41a',
    success: '#52c41a',
    failed: '#ff4d4f',
    error: '#ff4d4f',
    cancelled: '#ff9800'
  };

  const statusColor = statusColors[status] || statusColors.pending;
  const nodeIcon = pickNodeIcon(nodeType, label);

  const useCartoonPreflight = nodeType === 'preflight';
  const useCartoonLive = nodeType === 'execute_live';
  const useCartoonScript = nodeType === 'generate_script';

  const rootClass = [
    'gwe-workflow-node',
    'gwe-workflow-node--action',
    status === 'running' && 'gwe-workflow-node--running',
  ].filter(Boolean).join(' ');

  const handles = (
    <>
      <Handle type="target" position={Position.Top} id="top" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="target" position={Position.Left} id="target-left" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      <Handle type="target" position={Position.Right} id="target-right" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      <Handle type="source" position={Position.Top} id="source-top" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="source" position={Position.Bottom} id="bottom" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="source" position={Position.Left} id="left" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      <Handle type="source" position={Position.Right} id="right" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
    </>
  );

  return (
    <div
      className={rootClass}
      style={{ borderColor: statusColor }}
      data-node-status={status}
      onContextMenu={handleContextMenu}
    >
      {handles}

      <div className="gwe-workflow-node__action-layout">
        <div className="gwe-workflow-node__icon-box">
          {useCartoonPreflight ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonPreflight status={status} />
            </div>
          ) : useCartoonLive ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonExecuteLive status={status} />
            </div>
          ) : useCartoonScript ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonGenerateScript status={status} />
            </div>
          ) : (
            <IonIcon icon={nodeIcon} className="gwe-workflow-node__icon" />
          )}
        </div>
        <EditableLabel
          label={label} editing={editing} setEditing={setEditing}
          editValue={editValue} setEditValue={setEditValue}
          commitRename={commitRename} inputRef={inputRef}
          className="gwe-workflow-node__label--below"
        />
        {(toolCount > 0 || hasCustomCode) && (
          <div className="gwe-workflow-node__badges">
            {toolCount > 0 && (
              <div className="gwe-workflow-node__badge gwe-workflow-node__badge--tools">
                {toolCount} tool{toolCount > 1 ? 's' : ''}
              </div>
            )}
            {hasCustomCode && (
              <div className="gwe-workflow-node__badge gwe-workflow-node__badge--custom">
                custom
              </div>
            )}
          </div>
        )}
      </div>

      {showAddButton && (
        <button
          className="gwe-workflow-node__add-btn"
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            actions?.onRequestAddNode(nodeId, rect);
          }}
          title="Insert node after this step"
        >
          +
        </button>
      )}
    </div>
  );
};
